import ErrorNotifier from 'app/scripts/services/error-notifier';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from 'lodash-es';
import { getSpotConfig, setSpotConfig, TConfigKey, TConfigType } from '.';
import { getSpotId } from '../../../services/app-state';

type TSpotConfigResponse = [
  TConfigType[],
  (
    newValues: TConfigKey[] | TConfigKey,
    spotName?: string,
    onResult?: (isSuccess: boolean) => void
  ) => void,
  { loading: boolean; saving: boolean; errorFetching: boolean; errorSaving: boolean },
  (getDefaultConfig?: boolean) => void
];
type TDefaultValues = { [key: string]: TConfigType } | TConfigType;

/**
 *
 * @param keys - the fields you want to require from the tenant db. Annotated as: `tenant.namespace.key` or `tenant.namespace` (if namespace flag is on)
 * @param defaultValues - default values in case the get request would fail, optional
 * @param byNameSpace - flag indicates to get config by namespace and not single key
 * @param lazyFetch - flag for disable immediate fetching, we can fetch config later by using 'fetchInitialConfig'
 * @returns array contains the following:
 * configValues - an array with the values of the requested fields at the same order given to the hook.
 * updateSpotConfig - an updater function to set new values for the given keys, same as old api.
 * loading - a flag indicates if the get request is still loading,
 * when loading set to false it means the initial values are available.
 */
export default function useSpotConfig(
  keys: string | string[] = [],
  defaultValues = {} as TDefaultValues,
  byNameSpace?: boolean,
  lazyFetch?: boolean
): TSpotConfigResponse {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorFetching, setErrorFetching] = useState<boolean>(false);
  const [errorSaving, setErrorSaving] = useState<boolean>(false);
  const spotId: string = getSpotId();
  const validKeys: string[] = Array.isArray(keys) ? keys : [keys];
  const validValues = Array.isArray(keys) ? defaultValues : { [keys]: defaultValues };

  const configKeysArray: TConfigKey[] = useMemo(
    () =>
      validKeys.map((curr: string) => {
        return {
          key: curr,
          value: validValues[curr],
          updated_at: new Date()
        };
      }),
    [defaultValues, keys]
  );

  const [innerConfig, setInnerConfig] = useState<TConfigKey[]>(configKeysArray);
  const [configValues, setConfigValues] = useState<TConfigType[]>(
    innerConfig.map(con => con.value)
  );

  const fetchInitialConfig = async (getDefaultConfig?: boolean) => {
    if (validKeys.length === 0) {
      return;
    }

    try {
      setLoading(true);
      const requiredSpotId = getDefaultConfig ? 'default' : spotId;
      const fetchedConfig = await getSpotConfig(requiredSpotId, validKeys, { byNameSpace });
      if (byNameSpace) {
        setConfigValues(fetchedConfig);
      } else {
        const newInnerConfig = innerConfig.map(
          // We are leaving the full key here as `db.namespace.key` for old api support.
          // Also notice we are overriding the defaultValue
          con => {
            const { group_id, created_at, key, ...rest } = get(fetchedConfig, con.key) || con;
            const newCon = { key: con.key, ...rest };
            // Override updated_at if we are fetching the default group settings, this for later saving without issues
            if (getDefaultConfig) {
              newCon.updated_at = new Date();
            }
            return newCon;
          }
        );
        const newValues = newInnerConfig.map(con => con.value);
        setInnerConfig(newInnerConfig);
        setConfigValues(newValues);
      }

      setLoading(false);
    } catch (err) {
      console.error('[USE-CONFIG]:', err);
      ErrorNotifier.notifyError(`Failed to fetch spot config (from useSpotConfig)`, {
        fields: validKeys
      });

      setLoading(false);
      setErrorFetching(true);
    }
  };

  useEffect(() => {
    if (loading && !lazyFetch) {
      fetchInitialConfig();
    }
  }, [loading]);

  // Merge the innerConfig object with the request coming from
  // the setConfig hook provided to the component
  const createInnerConfigFromNewConfig = useCallback(
    (newConfig, after?: boolean) => {
      return innerConfig.reduce((acc, curr) => {
        const { group_id, created_at, ...rest } =
          newConfig.find(con =>
            // There is a difference between the structure of the request and the response
            after ? `tenant.${con.namespace}.${con.key}` === curr.key : con.key === curr.key
          ) || {};
        const updated: TConfigKey = { ...curr, ...rest, key: curr.key };
        return [...acc, updated];
      }, []);
    },
    [innerConfig]
  );

  // Creates a valid request payload to the host-panel bed
  const createSetConfigPayload = useCallback(
    newConfig => {
      return newConfig.reduce((acc, curr) => {
        const oldConfig = innerConfig.find(con => con.key === curr.key) || {};
        const updated = { ...oldConfig, ...curr };
        return [...acc, updated];
      }, []);
    },
    [innerConfig]
  );

  const updateSpotConfig = useCallback(
    (
      newValues: TConfigKey | TConfigKey[] = [],
      spotName: string = spotId,
      onResult?: (isSuccess: boolean) => void
    ): void => {
      setSaving(true);
      setErrorSaving(false);

      const arrayValues = Array.isArray(newValues) ? newValues : [newValues];
      // New inner config for the react hook
      const newInnerConfig = createInnerConfigFromNewConfig(arrayValues);

      // Config to be sent to the host-panel backend
      const configToBed = createSetConfigPayload(arrayValues);

      setSpotConfig(spotId, configToBed, spotName)
        .then(res => {
          const newConfig = res.data;
          setInnerConfig(createInnerConfigFromNewConfig(newConfig, true));
          setSaving(false);
          if (onResult) {
            onResult(true);
          }
        })
        // For now just console.error this
        .catch(err => {
          console.error('[USE-CONFIG]:', err);
          ErrorNotifier.notifyError(`Failed to update spot config (from useSpotConfig)`, {
            newValues
          });
          setSaving(false);
          setErrorSaving(true);
          if (onResult) {
            onResult(false);
          }
        });

      if (!byNameSpace) {
        // We are not waiting for backend response and immediately
        // set the local config in terms of optimistic view
        setConfigValues(newInnerConfig.map(con => con.value));
      }
    },
    [keys, spotId, saving, innerConfig]
  );

  return [
    configValues,
    updateSpotConfig,
    { loading, saving, errorFetching, errorSaving },
    fetchInitialConfig
  ];
}
